import { getGeneralConfig , getUserGeneralConfig, getEntitiesByTenant, getEntities, getEntityById, getEntityTypes, getEntityLogo } from "../Screens/dataSaver";

// Cached fetch for General Config to avoid multiple calls on app start

let getGeneralConfigCache = null;
let getGeneralConfigPendingRequest = null;
let getGeneralConfigLastFetch = 0;
const TTL = 5 * 60 * 1000;

export function getCachedGeneralConfig() {
  return getGeneralConfigCache;
}

export async function fetchGeneralConfig({ force = false, ttl = TTL } = {}) {
  const now = Date.now();
  if (!force && getGeneralConfigCache && now - getGeneralConfigLastFetch < ttl) return getGeneralConfigCache;
  if (getGeneralConfigPendingRequest) return getGeneralConfigPendingRequest;

  console.log("GeneralConfig Data fetching from server");

  getGeneralConfigPendingRequest = getGeneralConfig()
    .then(({ data }) => {
      console.log("GeneralConfig Data fetched from server", data);
      getGeneralConfigCache = data || {};
      getGeneralConfigLastFetch = Date.now();
      return getGeneralConfigCache;
    })
    .finally(() => (getGeneralConfigPendingRequest = null));

  return getGeneralConfigPendingRequest;
}

// Cached fetch for User General Config to avoid multiple calls on app start
let getUserGeneralConfigCache = null;
let getUserGeneralConfigPendingRequest = null;
let getUserGeneralConfigLastFetch = 0;

export function getCachedUserGeneralConfig() {
  return getUserGeneralConfigCache;
}

export async function fetchUserGeneralConfig({ force = false, ttl = TTL } = {}) {
  const now = Date.now();
  if (!force && getUserGeneralConfigCache && now - getUserGeneralConfigLastFetch < ttl) return getUserGeneralConfigCache;
  if (getUserGeneralConfigPendingRequest) return getUserGeneralConfigPendingRequest;

  getUserGeneralConfigPendingRequest = getUserGeneralConfig()
    .then(({ data }) => {
      getUserGeneralConfigCache = data || {};
      getUserGeneralConfigLastFetch = Date.now();
      return getUserGeneralConfigCache;
    })
    .finally(() => (getUserGeneralConfigPendingRequest = null));

  return getUserGeneralConfigPendingRequest;
}

// Cached fetch for Entity Details to avoid multiple calls on app start
let entityCache = null;
let entityPendingRequest = null;
let entityLastFetch = 0;

export async function fetchEntityDetails({ tenantId, force = false, ttl = TTL } = {}) {
  const now = Date.now();
  if (!force && entityCache && now - entityLastFetch < ttl) return entityCache;
  if (entityPendingRequest) return entityPendingRequest;

  if (!tenantId) throw new Error("fetchEntityDetails: tenantId is required");

  entityPendingRequest = getEntitiesByTenant(tenantId)
    .then(({ data }) => {
      entityCache = data || {};
      entityLastFetch = Date.now();
      return entityCache;
    })
    .finally(() => (entityPendingRequest = null));

  return entityPendingRequest;
}

// Cached fetch for All Entities to avoid multiple calls
let allEntitiesCache = null;
let allEntitiesPendingRequest = null;
let allEntitiesLastFetch = 0;

export async function fetchAllEntities({ force = false, ttl = TTL } = {}) {
  const now = Date.now();
  if (!force && allEntitiesCache && now - allEntitiesLastFetch < ttl) return allEntitiesCache;
  if (allEntitiesPendingRequest) return allEntitiesPendingRequest;

  allEntitiesPendingRequest = getEntities()
    .then(({ data }) => {
      allEntitiesCache = data || {};
      allEntitiesLastFetch = Date.now();
      return allEntitiesCache;
    })
    .finally(() => (allEntitiesPendingRequest = null));

  return allEntitiesPendingRequest;
}

// Cached fetch for Entity by ID  to avoid duplicate calls
const entityByIdCache = new Map(); 
const entityByIdPending = new Map(); 
const entityByIdLastFetch = new Map(); 

export async function fetchEntityById({ id, force = false, ttl = TTL } = {}) {
  const now = Date.now();
  if (!id) throw new Error("fetchEntityById: id is required");

  if (!force && entityByIdCache.has(id) && now - (entityByIdLastFetch.get(id) || 0) < ttl) {
    return entityByIdCache.get(id);
  }

  if (entityByIdPending.has(id)) {
    return entityByIdPending.get(id);
  }

  const pending = getEntityById(id)
    .then(({ data }) => {
      entityByIdCache.set(id, data || {});
      entityByIdLastFetch.set(id, Date.now());
      return entityByIdCache.get(id);
    })
    .finally(() => entityByIdPending.delete(id));

  entityByIdPending.set(id, pending);
  return pending;
}

// Cached fetch for Entity Types
let entityTypesCache = null;
let entityTypesPendingRequest = null;
let entityTypesLastFetch = 0;

export async function fetchEntityTypes({ force = false, ttl = TTL } = {}) {
  const now = Date.now();
  if (!force && entityTypesCache && now - entityTypesLastFetch < ttl) return entityTypesCache;
  if (entityTypesPendingRequest) return entityTypesPendingRequest;

  entityTypesPendingRequest = getEntityTypes()
    .then(({ data }) => {
      entityTypesCache = data || [];
      entityTypesLastFetch = Date.now();
      return entityTypesCache;
    })
    .finally(() => (entityTypesPendingRequest = null));

  return entityTypesPendingRequest;
}

// Cached fetch for Entity Logo by tenant
const entityLogoCache = new Map();
const entityLogoPending = new Map();
const entityLogoLastFetch = new Map();

export async function fetchEntityLogo({ tenantId, force = false, ttl = TTL } = {}) {
  const now = Date.now();
  if (!tenantId) throw new Error("fetchEntityLogo: tenantId is required");

  if (!force && entityLogoCache.has(tenantId) && now - (entityLogoLastFetch.get(tenantId) || 0) < ttl) {
    return entityLogoCache.get(tenantId);
  }

  if (entityLogoPending.has(tenantId)) {
    return entityLogoPending.get(tenantId);
  }

  const pending = getEntityLogo(tenantId)
    .then(({ data }) => {
      entityLogoCache.set(tenantId, data || null);
      entityLogoLastFetch.set(tenantId, Date.now());
      return entityLogoCache.get(tenantId);
    })
    .finally(() => entityLogoPending.delete(tenantId));

  entityLogoPending.set(tenantId, pending);
  return pending;
}